import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import {
  Building2, Target, Receipt, Gauge, Layers, TrendingUp, Bell,
  ChevronDown, ChevronUp, CheckCircle2, Loader2,
} from 'lucide-react'
import { PageHeader } from '../components/ui/PageHeader'
import { AlertBanner } from '../components/ui/AlertBanner'
import { IndustrySelector } from '../components/ui/IndustrySelector'
import { useTenant, useUpdateTenant, TenantSettings } from '../hooks/useTenant'
import { DEFAULT_THRESHOLDS } from '../hooks/useTenant'
import { useUpdateIndustry } from '../hooks/useIndustry'
import type { IndustrySlug } from '../lib/industries'

// ── Tipi form ─────────────────────────────────────────────────────────────────

interface SettingsFormData {
  // Profilo
  name: string
  currency: string
  fiscal_year_start: number
  service_lines_raw: string   // stringa separata da virgola
  // Target finanziari (undefined = non impostato, mostra il placeholder)
  target_annuale: number | undefined
  target_mensile: number | undefined
  target_margine_pct: number
  target_margine_commessa_pct: number
  // Fatturazione
  target_fatturazione_giorni: number
  target_dso_giorni: number
  // Operativi
  ore_disponibili_mensili: number
  target_utilization_pct: number
  recurring_growth_rate_pct: number   // mostrato come % (0-100), convertito in 0-1 al salvataggio
  // Pipeline
  pipeline_weight_confirmed_pct: number
  pipeline_weight_probable_pct: number
  // Alert thresholds
  revenue_budget_positive: number
  revenue_budget_warning: number
  margin_positive: number
  margin_warning: number
  dso_positive: number
  dso_warning: number
  overdue_positive: number
  overdue_warning: number
  project_margin_positive: number
  project_margin_warning: number
  utilization_positive: number
  utilization_warning: number
}

// ── Sezione collassabile ──────────────────────────────────────────────────────

function Section({
  icon: Icon,
  title,
  subtitle,
  color,
  children,
  defaultOpen = false,
}: {
  icon: React.ElementType
  title: string
  subtitle: string
  color: string
  children: React.ReactNode
  defaultOpen?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <div className="km-panel overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-4 p-5 text-left hover:bg-white/[0.02] transition-colors"
      >
        <div className={`h-9 w-9 rounded-xl flex items-center justify-center shrink-0 ${color}`}>
          <Icon size={16} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[--km-text-primary]">{title}</p>
          <p className="text-xs text-[--km-text-muted] mt-0.5">{subtitle}</p>
        </div>
        {open
          ? <ChevronUp size={16} className="text-[--km-text-muted] shrink-0" />
          : <ChevronDown size={16} className="text-[--km-text-muted] shrink-0" />}
      </button>
      {open && (
        <div className="px-5 pb-5 border-t border-white/[0.06]">
          <div className="pt-5">{children}</div>
        </div>
      )}
    </div>
  )
}

// ── Campo form ────────────────────────────────────────────────────────────────

function Field({
  label,
  hint,
  children,
}: {
  label: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-[--km-text-secondary] mb-1.5">{label}</label>
      {children}
      {hint && <p className="mt-1 text-[11px] text-[--km-text-muted]">{hint}</p>}
    </div>
  )
}

const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className = '', ...props }, ref) {
    return (
      <input
        ref={ref}
        {...props}
        className={`w-full rounded-xl border border-white/[0.08] bg-white/[0.04] px-3 py-2
          text-sm text-[--km-text-primary] placeholder-[--km-text-disabled]
          focus:outline-none focus:border-indigo-500/50 focus:bg-white/[0.06] transition-colors
          [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none
          [&::-webkit-inner-spin-button]:appearance-none ${className}`}
      />
    )
  }
)

const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className = '', children, ...props }, ref) {
    return (
    <select
      ref={ref}
      {...props}
      className={`w-full rounded-xl border border-white/[0.08] bg-[#0d1321] px-3 py-2
        text-sm text-[--km-text-primary]
        focus:outline-none focus:border-indigo-500/50 transition-colors ${className}`}
    >
      {children}
    </select>
  )
  }
)

// ── Riga soglia (positive + warning affiancati) ───────────────────────────────

function ThresholdRow({
  label,
  positiveLabel,
  warningLabel,
  positiveKey,
  warningKey,
  unit = '%',
  register,
}: {
  label: string
  positiveLabel: string
  warningLabel: string
  positiveKey: keyof SettingsFormData
  warningKey: keyof SettingsFormData
  unit?: string
  register: ReturnType<typeof useForm<SettingsFormData>>['register']
}) {
  return (
    <div>
      <p className="text-xs font-medium text-[--km-text-secondary] mb-2">{label}</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label={`${positiveLabel} (${unit})`}>
          <div className="relative">
            <Input
              type="number"
              step="1"
              {...register(positiveKey, { valueAsNumber: true })}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-emerald-400 font-medium">Verde</span>
          </div>
        </Field>
        <Field label={`${warningLabel} (${unit})`}>
          <div className="relative">
            <Input
              type="number"
              step="1"
              {...register(warningKey, { valueAsNumber: true })}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-amber-400 font-medium">Giallo</span>
          </div>
        </Field>
      </div>
    </div>
  )
}

// ── Bottone salva sezione ─────────────────────────────────────────────────────

function SaveButton({ loading, saved }: { loading: boolean; saved: boolean }) {
  return (
    <button
      type="submit"
      disabled={loading}
      className="mt-5 flex items-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-500
        disabled:opacity-50 px-4 py-2 text-sm font-semibold text-white transition-colors"
    >
      {loading
        ? <><Loader2 size={14} className="animate-spin" /> Salvataggio…</>
        : saved
        ? <><CheckCircle2 size={14} className="text-emerald-300" /> Salvato</>
        : 'Salva modifiche'}
    </button>
  )
}

// ── Componente principale ─────────────────────────────────────────────────────

export function Settings() {
  const { data: tenant, isLoading, isError } = useTenant()
  const update = useUpdateTenant()
  const updateIndustry = useUpdateIndustry()

  // Stato locale per il settore aziendale (OB.1)
  const [industry, setIndustry] = useState<IndustrySlug | null>(null)
  const [industrySaved, setIndustrySaved] = useState(false)

  useEffect(() => {
    if (tenant?.settings?.industry) {
      setIndustry(tenant.settings.industry as IndustrySlug)
    }
  }, [tenant?.settings?.industry])

  const { register, handleSubmit, reset } = useForm<SettingsFormData>({
    defaultValues: {
      name: '',
      currency: 'EUR',
      fiscal_year_start: 1,
      service_lines_raw: 'SG, LIC, DEV, RIV, PS',
      target_annuale: undefined,
      target_mensile: undefined,
      target_margine_pct: 30,
      target_margine_commessa_pct: 15,
      target_fatturazione_giorni: 60,
      target_dso_giorni: 45,
      ore_disponibili_mensili: 160,
      target_utilization_pct: 75,
      recurring_growth_rate_pct: 0,
      pipeline_weight_confirmed_pct: 100,
      pipeline_weight_probable_pct: 50,
      revenue_budget_positive: DEFAULT_THRESHOLDS.revenue_budget_positive,
      revenue_budget_warning: DEFAULT_THRESHOLDS.revenue_budget_warning,
      margin_positive: DEFAULT_THRESHOLDS.margin_positive,
      margin_warning: DEFAULT_THRESHOLDS.margin_warning,
      dso_positive: DEFAULT_THRESHOLDS.dso_positive,
      dso_warning: DEFAULT_THRESHOLDS.dso_warning,
      overdue_positive: DEFAULT_THRESHOLDS.overdue_positive,
      overdue_warning: DEFAULT_THRESHOLDS.overdue_warning,
      project_margin_positive: DEFAULT_THRESHOLDS.project_margin_positive,
      project_margin_warning: DEFAULT_THRESHOLDS.project_margin_warning,
      utilization_positive: DEFAULT_THRESHOLDS.utilization_positive,
      utilization_warning: DEFAULT_THRESHOLDS.utilization_warning,
    },
  })

  // Popola il form quando i dati tenant arrivano o vengono aggiornati
  useEffect(() => {
    if (!tenant) return
    const ts = tenant.settings ?? {}
    const thr2 = { ...DEFAULT_THRESHOLDS, ...(ts.alert_thresholds ?? {}) }
    reset({
      name: tenant.name?.trim() ?? '',
      currency: ts.currency ?? 'EUR',
      fiscal_year_start: ts.fiscal_year_start ?? 1,
      service_lines_raw: (ts.service_lines?.length ? ts.service_lines : ['SG', 'LIC', 'DEV', 'RIV', 'PS']).join(', '),
      // Usa undefined (non 0) così il placeholder è visibile quando il target non è impostato
      target_annuale: ts.target_annuale || undefined,
      target_mensile: ts.target_mensile || undefined,
      target_margine_pct: ts.target_margine_pct ?? 30,
      target_margine_commessa_pct: ts.target_margine_commessa_pct ?? 15,
      target_fatturazione_giorni: ts.target_fatturazione_giorni ?? 60,
      target_dso_giorni: ts.target_dso_giorni ?? 45,
      ore_disponibili_mensili: ts.ore_disponibili_mensili ?? 160,
      target_utilization_pct: ts.target_utilization_pct ?? 75,
      recurring_growth_rate_pct: (((ts as Record<string, unknown>).recurring_growth_rate as number | undefined) ?? ((ts as Record<string, unknown>).opex_growth_rate as number | undefined) ?? 0) * 100,
      pipeline_weight_confirmed_pct: (ts.pipeline_weight_confirmed ?? 1.0) * 100,
      pipeline_weight_probable_pct: (ts.pipeline_weight_probable ?? 0.5) * 100,
      revenue_budget_positive: thr2.revenue_budget_positive,
      revenue_budget_warning: thr2.revenue_budget_warning,
      margin_positive: thr2.margin_positive,
      margin_warning: thr2.margin_warning,
      dso_positive: thr2.dso_positive,
      dso_warning: thr2.dso_warning,
      overdue_positive: thr2.overdue_positive,
      overdue_warning: thr2.overdue_warning,
      project_margin_positive: thr2.project_margin_positive,
      project_margin_warning: thr2.project_margin_warning,
      utilization_positive: thr2.utilization_positive,
      utilization_warning: thr2.utilization_warning,
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.id, tenant?.updated_at])

  const [savedSection, setSavedSection] = useState<string | null>(null)

  const save = (section: string, patch: Partial<TenantSettings>, name?: string) => {
    update.mutate(
      { name, settings: patch },
      {
        onSuccess: () => {
          setSavedSection(section)
          setTimeout(() => setSavedSection(null), 2500)
        },
      },
    )
  }

  if (isError) return (
    <section className="space-y-6">
      <PageHeader
        title="Impostazioni azienda"
        subtitle="Configura target, soglie e parametri operativi"
        icon={Building2}
      />
      <AlertBanner severity="critical" message="Impossibile caricare le impostazioni azienda. Ricarica la pagina o contatta il supporto." />
    </section>
  )

  if (isLoading) return (
    <section className="space-y-6">
      <div className="h-16 rounded-2xl animate-shimmer" />
      <div className="space-y-3">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-20 rounded-2xl animate-shimmer" />)}
      </div>
    </section>
  )

  return (
    <section aria-label="Impostazioni azienda" className="space-y-6">
      <PageHeader
        title="Impostazioni azienda"
        subtitle="Configura target, soglie e parametri operativi"
        icon={Building2}
      />

      {update.isError && (
        <AlertBanner severity="critical" message="Errore nel salvataggio. Riprovare." />
      )}

      <div className="space-y-3">

        {/* 0 — Settore aziendale */}
        <Section
          icon={Layers}
          title="Settore aziendale"
          subtitle="Ottimizza il mapping dei file e i KPI per il tuo tipo di business"
          color="bg-gradient-to-br from-violet-500 to-purple-700"
          defaultOpen={!tenant?.settings?.industry}
        >
          <div className="space-y-4">
            <p className="text-xs text-[--km-text-muted] leading-relaxed">
              La selezione del settore permette a Koremargin di suggerire automaticamente i campi
              corretti durante il caricamento dei file e di mostrare in primo piano i KPI più
              rilevanti per il tuo business. Puoi cambiare questa scelta in qualsiasi momento.
            </p>
            <IndustrySelector
              value={industry}
              onChange={setIndustry}
              showOther
            />
            <button
              type="button"
              disabled={updateIndustry.isPending}
              onClick={() => {
                updateIndustry.mutate(industry, {
                  onSuccess: () => {
                    setIndustrySaved(true)
                    setTimeout(() => setIndustrySaved(false), 2500)
                  },
                })
              }}
              className="flex items-center gap-2 rounded-xl bg-indigo-600 hover:bg-indigo-500
                disabled:opacity-50 px-4 py-2 text-sm font-semibold text-white transition-colors"
            >
              {updateIndustry.isPending
                ? <><Loader2 size={14} className="animate-spin" /> Salvataggio…</>
                : industrySaved
                ? <><CheckCircle2 size={14} className="text-emerald-300" /> Salvato</>
                : 'Salva settore'}
            </button>
          </div>
        </Section>

        {/* 1 — Profilo azienda */}
        <Section
          icon={Building2}
          title="Profilo azienda"
          subtitle="Nome, valuta, anno fiscale e linee di servizio"
          color="bg-gradient-to-br from-indigo-500 to-purple-600"
          defaultOpen
        >
          <form
            noValidate
            onSubmit={handleSubmit(data => save('profilo', {
              currency: data.currency,
              fiscal_year_start: Number(data.fiscal_year_start),
              service_lines: data.service_lines_raw
                .split(',')
                .map(s => s.trim())
                .filter(Boolean),
            }, data.name.trim() || undefined))}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Nome azienda">
                <Input type="text" placeholder="Es. Acme Srl" {...register('name')} />
              </Field>
              <Field label="Valuta">
                <Select {...register('currency')}>
                  <option value="EUR">EUR — Euro</option>
                  <option value="USD">USD — Dollaro USA</option>
                  <option value="GBP">GBP — Sterlina</option>
                  <option value="CHF">CHF — Franco svizzero</option>
                </Select>
              </Field>
              <Field label="Inizio anno fiscale (mese)">
                <Select {...register('fiscal_year_start', { valueAsNumber: true })}>
                  {['Gennaio','Febbraio','Marzo','Aprile','Maggio','Giugno',
                    'Luglio','Agosto','Settembre','Ottobre','Novembre','Dicembre'].map((m, i) => (
                    <option key={i + 1} value={i + 1}>{m}</option>
                  ))}
                </Select>
              </Field>
              <Field label="Linee di servizio" hint="Separate da virgola. Es: SG, LIC, DEV, RIV, PS. Se vuoto, vengono usate le categorie predefinite.">
                <Input
                  type="text"
                  placeholder="SG, LIC, DEV, RIV, PS"
                  {...register('service_lines_raw')}
                />
              </Field>
            </div>
            <SaveButton loading={update.isPending && savedSection === null} saved={savedSection === 'profilo'} />
          </form>
        </Section>

        {/* 2 — Target finanziari */}
        <Section
          icon={Target}
          title="Target finanziari"
          subtitle="Revenue, margine lordo e soglia commesse a rischio"
          color="bg-gradient-to-br from-emerald-500 to-teal-600"
        >
          <form
            noValidate
            onSubmit={handleSubmit(data => save('finanziari', {
              target_annuale: Number(data.target_annuale) || 0,
              target_mensile: Number(data.target_mensile) || 0,
              target_margine_pct: Number(data.target_margine_pct) || 0,
              target_margine_commessa_pct: Number(data.target_margine_commessa_pct) || 0,
            }))}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Revenue annuale target (€)" hint="Usato per calcolare % raggiungimento budget in Executive Cockpit">
                <Input type="number" step="1000" min="0" placeholder="Es. 1.000.000" {...register('target_annuale', { valueAsNumber: true })} />
              </Field>
              <Field label="Revenue mensile target (€)" hint="Opzionale — per confronto budget/actual mensile">
                <Input type="number" step="100" min="0" placeholder="Es. 83.000" {...register('target_mensile', { valueAsNumber: true })} />
              </Field>
              <Field label="Gross margin % target" hint="Soglia verde per la KPICard Gross Margin">
                <Input type="number" step="0.5" min="0" max="100" {...register('target_margine_pct', { valueAsNumber: true })} />
              </Field>
              <Field label="Margine commessa a rischio (%)" hint="Commesse con margine inferiore a questa soglia sono considerate a rischio">
                <Input type="number" step="0.5" min="0" max="100" {...register('target_margine_commessa_pct', { valueAsNumber: true })} />
              </Field>
            </div>
            <SaveButton loading={update.isPending} saved={savedSection === 'finanziari'} />
          </form>
        </Section>

        {/* 3 — Fatturazione e incasso */}
        <Section
          icon={Receipt}
          title="Fatturazione e incasso"
          subtitle="Termini di pagamento standard e DSO target"
          color="bg-gradient-to-br from-blue-500 to-cyan-600"
        >
          <form
            noValidate
            onSubmit={handleSubmit(data => save('fatturazione', {
              target_fatturazione_giorni: Number(data.target_fatturazione_giorni),
              target_dso_giorni: Number(data.target_dso_giorni),
            }))}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Termini di pagamento standard" hint="Default applicato alle nuove fatture">
                <Select {...register('target_fatturazione_giorni', { valueAsNumber: true })}>
                  <option value={30}>30 giorni</option>
                  <option value={60}>60 giorni</option>
                  <option value={90}>90 giorni</option>
                  <option value={120}>120 giorni</option>
                </Select>
              </Field>
              <Field label="DSO target (giorni)" hint="Days Sales Outstanding attesi — determina la soglia verde del DSO">
                <Input type="number" step="1" min="0" {...register('target_dso_giorni', { valueAsNumber: true })} />
              </Field>
            </div>
            <SaveButton loading={update.isPending} saved={savedSection === 'fatturazione'} />
          </form>
        </Section>

        {/* 4 — Target operativi */}
        <Section
          icon={Gauge}
          title="Target operativi e produttività"
          subtitle="Ore disponibili, utilization e crescita ricavi ricorrenti"
          color="bg-gradient-to-br from-violet-500 to-purple-600"
        >
          <form
            noValidate
            onSubmit={handleSubmit(data => save('operativi', {
              ore_disponibili_mensili: Number(data.ore_disponibili_mensili),
              target_utilization_pct: Number(data.target_utilization_pct),
              recurring_growth_rate: Number(data.recurring_growth_rate_pct) / 100,
            }))}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Ore disponibili mensili per risorsa" hint="Base per il calcolo dell'utilization %">
                <Input type="number" step="1" min="0" {...register('ore_disponibili_mensili', { valueAsNumber: true })} />
              </Field>
              <Field label="Utilization % target" hint="% ore produttive su disponibili attesa">
                <Input type="number" step="1" min="0" max="100" {...register('target_utilization_pct', { valueAsNumber: true })} />
              </Field>
              <Field label="Tasso crescita ricavi ricorrenti (%)" hint="Applicato ai contratti volatili (mensili/bimestrali) nel forecast Recurring Revenue">
                <Input type="number" step="0.5" min="0" max="100" {...register('recurring_growth_rate_pct', { valueAsNumber: true })} />
              </Field>
            </div>
            <SaveButton loading={update.isPending} saved={savedSection === 'operativi'} />
          </form>
        </Section>

        {/* 5 — Pipeline e commerciale */}
        <Section
          icon={TrendingUp}
          title="Pipeline e commerciale"
          subtitle="Pesi per il calcolo della pipeline pesata"
          color="bg-gradient-to-br from-orange-500 to-amber-600"
        >
          <form
            noValidate
            onSubmit={handleSubmit(data => save('pipeline', {
              pipeline_weight_confirmed: Number(data.pipeline_weight_confirmed_pct) / 100,
              pipeline_weight_probable: Number(data.pipeline_weight_probable_pct) / 100,
            }))}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Peso ordinato confermato (%)" hint="Default 100% — incluso integralmente nella pipeline pesata">
                <Input type="number" step="5" min="0" max="100" {...register('pipeline_weight_confirmed_pct', { valueAsNumber: true })} />
              </Field>
              <Field label="Peso ordinato probabile (%)" hint="Default 50% — incluso parzialmente nella pipeline pesata">
                <Input type="number" step="5" min="0" max="100" {...register('pipeline_weight_probable_pct', { valueAsNumber: true })} />
              </Field>
            </div>
            <SaveButton loading={update.isPending} saved={savedSection === 'pipeline'} />
          </form>
        </Section>

        {/* 6 — Soglie alert */}
        <Section
          icon={Bell}
          title="Soglie alert"
          subtitle="Determina i colori verde / giallo / rosso delle KPI card"
          color="bg-gradient-to-br from-rose-500 to-pink-600"
        >
          <form
            noValidate
            onSubmit={handleSubmit(data => save('alert', {
              alert_thresholds: {
                revenue_budget_positive: Number(data.revenue_budget_positive),
                revenue_budget_warning: Number(data.revenue_budget_warning),
                margin_positive: Number(data.margin_positive),
                margin_warning: Number(data.margin_warning),
                dso_positive: Number(data.dso_positive),
                dso_warning: Number(data.dso_warning),
                overdue_positive: Number(data.overdue_positive),
                overdue_warning: Number(data.overdue_warning),
                project_margin_positive: Number(data.project_margin_positive),
                project_margin_warning: Number(data.project_margin_warning),
                utilization_positive: Number(data.utilization_positive),
                utilization_warning: Number(data.utilization_warning),
              },
            }))}
          >
            <div className="space-y-5">
              <p className="text-xs text-[--km-text-muted]">
                I valori "Verde" indicano performance ottimale. I valori "Giallo" indicano attenzione. Sotto il giallo → rosso.
              </p>

              <ThresholdRow
                label="Revenue vs Budget"
                positiveLabel="Verde se ≥"
                warningLabel="Giallo se ≥"
                positiveKey="revenue_budget_positive"
                warningKey="revenue_budget_warning"
                register={register}
              />
              <ThresholdRow
                label="Gross Margin %"
                positiveLabel="Verde se ≥"
                warningLabel="Giallo se ≥"
                positiveKey="margin_positive"
                warningKey="margin_warning"
                register={register}
              />
              <ThresholdRow
                label="DSO (giorni — valore basso è positivo)"
                positiveLabel="Verde se ≤"
                warningLabel="Giallo se ≤"
                positiveKey="dso_positive"
                warningKey="dso_warning"
                unit="gg"
                register={register}
              />
              <ThresholdRow
                label="Crediti Scaduti % (valore basso è positivo)"
                positiveLabel="Verde se ≤"
                warningLabel="Giallo se ≤"
                positiveKey="overdue_positive"
                warningKey="overdue_warning"
                register={register}
              />
              <ThresholdRow
                label="Margine commessa %"
                positiveLabel="Verde se ≥"
                warningLabel="Giallo se ≥"
                positiveKey="project_margin_positive"
                warningKey="project_margin_warning"
                register={register}
              />
              <ThresholdRow
                label="Utilization %"
                positiveLabel="Verde se ≥"
                warningLabel="Giallo se ≥"
                positiveKey="utilization_positive"
                warningKey="utilization_warning"
                register={register}
              />
            </div>
            <SaveButton loading={update.isPending} saved={savedSection === 'alert'} />
          </form>
        </Section>

      </div>
    </section>
  )
}
